<template>
  <a
    class="product-card"
    :class="{
      [$class('loading')]: !product,
      [modifiers]: modifiers,
    }"
    :href="product.url"
    :data-isbn="product.hyphenatedIsbn"
    :data-position="position"
    data-element-type="link"
    :data-product-badge="productBadge"
    :data-section-type="sectionType"
    :data-section-name="sectionName"
  >
    <div
      v-if="showFlairApp"
      class="product-card__flair-badge"
    >
      <flair-badges
        :product-id="product?.id || liquid?.id"
        auto-layout="default"
      />
    </div>
    <product-badge
      v-else
      :product="product"
      @render="(badge) => productBadge = badge"
    />
    <div
      class="product-card__image-container"
      :class="{
        [$class('disabled')]: !product.available || !product.publishedProduct,
      }"
    >
      <responsive-image
        class="product-card__image"
        :image="product.featuredImage"
        :lazyload="lazyload"
        min-max="200-500"
        ratio="3:4"
      />

      <button
        v-if="showQuickAddButton"
        class="product-card__atc"
        type="button"
        :title="$string('product.cta.add_to_cart')"
        @click.prevent="addItemToCart"
      >
        <icon-loading v-if="state === 'loading'" />
        <icon-add-to-cart v-else />
      </button>
    </div>

    <div class="product-card__footer">
      <h3
        class="
        product-card__title
        text-heading-m
      "
        :class="size === 'small' ? '' : 'text-heading-l-desktop'"
        v-text="product.title"
      />

      <div class="product-card__prices-wrapper">
        <product-prices
          v-if="showSchoolPrice"
          :price-label="$string('product.prices.school')"
          class="product-card__prices full-width"
          class-label="text-body-s-bold"
          class-price="text-body-s-bold"
          class-compare-at-price="text-other-sale-s"
          :compare-at-price="schoolCompareAtPrice"
          :price="schoolPrice"
        />

        <product-prices
          :price-label="showSchoolPrice ? $string('product.prices.retail') : ''"
          class="product-card__prices"
          :class="{'same-color': showSchoolPrice}"
          class-label="text-body-s-regular"
          class-price="text-body-s-bold"
          class-compare-at-price="text-other-sale-s"
          :compare-at-price="product?.compareAtPrice?.amount"
          :price="showSchoolPrice && schoolPrice !== itemPrice ? itemPrice : product?.price?.amount"
        />

        <p
          class="product-card__type text-body-s-bold"
          v-text="product.type"
        />
      </div>
    </div>
  </a>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {String} size - Card size modifier (small, or leave empty).
 * @param {String} placement - Card placement modifier (plp, or leave empty).
 * @param {Boolean} [lazyload] - Lazyload product image, defaults to true.
 * @param {Number} position - position of product in listing.
 * @param {String} sectionType - type of section product card is rendered in.
 * @param {String} sectionName - name of section product card is rendered in.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import ProductBadge from '~async/product-badge/product-badge'
import IconAddToCart from '~icons/general/add-to-cart.svg'
import IconLoading from '~icons/general/loading.svg'

export default {
  name: 'ProductCard',

  components: {
    ProductPrices,
    ResponsiveImage,
    IconAddToCart,
    IconLoading,
    ProductBadge,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    size: {
      type: String,
      default: '',
    },
    placement: {
      type: String,
      default: '',
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    position: {
      type: Number,
      default: 1,
    },
    sectionType: {
      type: String,
      default: '',
    },
    sectionName: {
      type: String,
      default: '',
    },
    variantPrice: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      state: 'ready',
      productBadge: '',
      showFlairApp: cnvs.settings.enableFlairApp,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    schoolPrice() {
      return this.getTagPrice(this.variantPrice?.priceschool)
    },

    itemPrice() {
      return this.getTagPrice(this.variantPrice?.rrpprice)
    },

    schoolCompareAtPrice() {
      return this.getTagPrice(this.variantPrice?.comparepriceschool)
    },

    modifiers() {
      return `${this.size} ${this.placement}`
    },

    customerRole() {
      return this.cart?.attributes?.customer_role
    },

    showSchoolPrice() {
      return this.schoolPrice &&
        this.customerRole === this.$string('general.multi_role_switcher.teacher_value') &&
        this.schoolPrice !== this.product?.price?.amount
    },

    /**
     * Compute if the Quick Add / Add to Cart button should be displayed.
     * @returns {Boolean}
     */
    showQuickAddButton() {
      const isProductAvailable =
        this.product?.firstAvailableVariant?.available &&
        this.product.publishedProduct

      const isDigitalProductLicense = this.product?.tags?.indexOf('digital-product-license-code') > -1
      const hasCustomDigitalProductCTA = this.$variable('settings.product.enableDigitalProductOverrideCTA') && isDigitalProductLicense

      const hasCustomCTA = hasCustomDigitalProductCTA || this.product?.hasOverrideCTA

      return isProductAvailable && !hasCustomCTA
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
      setShowError: 'product-eval-copy/setShowError',
    }),

    /**
     * Add to cart handler
     */
    async addItemToCart() {
      if (
        !this.product?.firstAvailableVariant?.available ||
        !this.product.publishedProduct
      ) {
        return
      }

      if (this.cart.items.some((item) => item.properties?.[this.$variable('constants.LINE_ITEM_PROPERTY_EVALUATION')])) {
        this.setShowError(true)

        return
      }

      clearTimeout(this.timeout)
      this.state = 'loading'

      if (cnvs.storybook) {
        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('slow'))

        return
      }

      const items = [
        {
          id: this.product?.firstAvailableVariant?.id,
          quantity: 1,
        },
      ]

      try {
        await this.addItem(items)
        this.state = 'ready'

        if (
          cnvs.settings.autoOpenCartDrawer &&
          this.getComponentExists('cart-drawer')
        ) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

      } catch (error) {
        this.state = 'ready'

        if (error?.description) {
          this.error = error.description
          return
        }

        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    /**
     * Get tag price
     * @param {String} tagPrefix
     * @return {number|boolean}
     */
    getTagPrice(data) {

      if (isNaN(data)) {
        return false
      }

      return data * 100
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
